import React from 'react';
import { ACE_ATTORNEY_JUDGMENT_MAX, ACE_ATTORNEY_QUESTION_MAX, formatString } from '@deadpan/shared';
import { useSocket } from '../../contexts/SocketContext';
import { useTranslations } from '../../hooks/useTranslations';

export const AceAttorneyPrompt: React.FC = () => {
  const { socket, gameState, currentRoom, myPlayerId } = useSocket();
  const t = useTranslations();
  const copy = t.games.aceAttorney;
  const [question, setQuestion] = React.useState('');
  const [judgment, setJudgment] = React.useState('');

  if (!gameState || !currentRoom) {
    return null;
  }

  const setterId = gameState.aceAttorneySetterId;
  const setter = currentRoom.players.find((player) => player.id === setterId);
  const isSetter = myPlayerId === setterId;
  const canSubmit = question.trim().length > 0 && judgment.trim().length > 0;

  const handleSubmit = () => {
    if (!socket || !isSetter || !canSubmit) return;
    socket.emit('client:ace-attorney-submit-prompt', question.trim(), judgment.trim());
  };

  return (
    <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8 w-[90%] max-w-[600px] border border-white/20">
      <div className="text-center mb-5">
        <p className="text-xs sm:text-sm font-semibold text-amber-700 mb-1">
          {formatString(copy.roundLabel, { round: String(currentRoom.currentRound || 1) })}
        </p>
        <p className="text-sm font-semibold text-amber-800 mb-1">
          {copy.setterLabel}: <span className="text-amber-950">{setter?.name ?? copy.setterLeft}</span>
        </p>
        <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-amber-700 to-orange-600 bg-clip-text text-transparent">
          {copy.promptTitle}
        </h2>
      </div>

      {isSetter ? (
        <div className="flex flex-col gap-4">
          <div className="p-4 rounded-xl border-2 border-amber-200 bg-amber-50 text-center">
            <p className="text-sm sm:text-base text-gray-700">{copy.promptInstruction}</p>
            <p className="text-sm font-bold text-amber-800 mt-3">{copy.youAreSetter}</p>
          </div>
          <label className="flex flex-col gap-2 text-sm font-semibold text-gray-700">
            {copy.questionLabel}
            <textarea
              value={question}
              onChange={(event) => setQuestion(event.target.value.slice(0, ACE_ATTORNEY_QUESTION_MAX))}
              rows={3}
              maxLength={ACE_ATTORNEY_QUESTION_MAX}
              placeholder={copy.questionPlaceholder}
              className="w-full p-3 border-2 border-amber-300 rounded-xl font-normal focus:outline-none focus:border-amber-500"
            />
            <span className="text-xs font-normal text-gray-500 text-right">
              {question.length}/{ACE_ATTORNEY_QUESTION_MAX}
            </span>
          </label>
          <label className="flex flex-col gap-2 text-sm font-semibold text-gray-700">
            {copy.promptJudgmentLabel}
            <textarea
              value={judgment}
              onChange={(event) => setJudgment(event.target.value.slice(0, ACE_ATTORNEY_JUDGMENT_MAX))}
              rows={4}
              maxLength={ACE_ATTORNEY_JUDGMENT_MAX}
              placeholder={copy.judgmentPlaceholder}
              className="w-full p-3 border-2 border-amber-300 rounded-xl font-normal focus:outline-none focus:border-amber-500"
            />
            <span className="text-xs font-normal text-gray-500 text-right">
              {judgment.length}/{ACE_ATTORNEY_JUDGMENT_MAX}
            </span>
          </label>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="w-full bg-gradient-to-r from-amber-600 to-orange-600 text-white py-3 rounded-xl font-bold disabled:from-gray-400 disabled:to-gray-400"
          >
            {copy.submitPrompt}
          </button>
        </div>
      ) : (
        <div className="p-4 rounded-xl border-2 border-stone-200 bg-stone-50 text-center">
          <p className="text-base sm:text-lg text-gray-700">{copy.waitingForSetter}</p>
        </div>
      )}
    </div>
  );
};
